import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Plus, Save, Trash2, Edit2, RefreshCw, Leaf, Activity, 
  Heart, Sun, Moon, Utensils, CheckCircle, X
} from 'lucide-react';

const API = '/api';

const LIFEFLO_CATEGORIES = [
  { value: 'movement', label: 'Movement', icon: Activity, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { value: 'mental', label: 'Mental', icon: Sun, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { value: 'routine', label: 'Routine', icon: Moon, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  { value: 'spiritual', label: 'Spiritual', icon: Heart, color: 'text-rose-400', bg: 'bg-rose-500/10' },
  { value: 'recovery', label: 'Recovery', icon: Leaf, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { value: 'nutrition_support', label: 'Nutrition Support', icon: Utensils, color: 'text-amber-400', bg: 'bg-amber-500/10' },
];

function getCategoryStyle(category) {
  return LIFEFLO_CATEGORIES.find(c => c.value === category) || LIFEFLO_CATEGORIES[0];
}

function DefinitionCard({ def, onEdit, onDelete, onToggleActive }) {
  const style = getCategoryStyle(def.category);
  const Icon = style.icon;
  
  return (
    <Card className={`bg-adm-surface/[0.02] border-white/[0.06] ${!def.is_active ? 'opacity-40' : ''}`} data-testid={`lifeflo-card-${def.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${style.color}`} />
            </div>
            <div>
              <div className="text-sm font-medium text-white/90">{def.name}</div>
              <div className="text-xs text-white/40 font-mono">{def.code}</div>
            </div>
          </div>
          <Badge className={`${style.bg} ${style.color} border-0 text-[9px]`}>{style.label}</Badge>
        </div>
        
        {def.default_instruction && (
          <div className="mt-3 text-xs text-white/50">{def.default_instruction}</div>
        )}
        
        <div className="mt-3 flex items-center gap-2 text-xs text-white/30">
          {def.recommended_duration && <span>{def.recommended_duration} min</span>}
          {def.default_context && <span>• {def.default_context}</span>}
        </div>
        
        <div className="mt-3 flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => onEdit(def)} className="h-7 px-2 text-[10px] text-white/40 hover:text-white">
            <Edit2 className="w-3 h-3 mr-1" /> Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onToggleActive(def)} className="h-7 px-2 text-[10px] text-white/40 hover:text-amber-400">
            {def.is_active ? <X className="w-3 h-3 mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
            {def.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(def.id)} className="h-7 px-2 text-[10px] text-white/40 hover:text-red-400">
            <Trash2 className="w-3 h-3 mr-1" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DefinitionEditDialog({ def, open, onClose, onSave }) {
  const [form, setForm] = useState(def || {});
  
  useEffect(() => {
    if (def) setForm(def);
    else setForm({ code: '', name: '', category: 'routine', default_instruction: '', recommended_duration: null, default_context: '', is_active: true });
  }, [def]);
  
  const handleSave = () => {
    onSave(form);
    onClose();
  };
  
  if (!open) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{def?.id ? 'Edit LifeFlō Definition' : 'Add LifeFlō Definition'}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Code *</label>
              <Input value={form.code || ''} onChange={e => setForm({...form, code: e.target.value})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="LF-001" />
            </div>
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Name *</label>
              <Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Morning Walk" />
            </div>
          </div>
          
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Category *</label>
            <Select value={form.category || ''} onValueChange={v => setForm({...form, category: v})}>
              <SelectTrigger className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {LIFEFLO_CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value} className="text-white text-xs">
                    <span className={c.color}>{c.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Default Instruction</label>
            <Input value={form.default_instruction || ''} onChange={e => setForm({...form, default_instruction: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Take a brisk 20-minute walk" />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Duration (minutes)</label>
              <Input type="number" value={form.recommended_duration || ''} onChange={e => setForm({...form, recommended_duration: e.target.value ? parseInt(e.target.value) : null})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="20" />
            </div>
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Default Context</label>
              <Input value={form.default_context || ''} onChange={e => setForm({...form, default_context: e.target.value})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Outdoors" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-white/50">Cancel</Button>
          <Button onClick={handleSave} disabled={!form.code || !form.name || !form.category} className="bg-emerald-600 hover:bg-emerald-700 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LifeFloDefinitions() {
  const { user } = useAuth();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [editDef, setEditDef] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  const token = localStorage.getItem('rf_access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  
  const fetchDefinitions = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('category', filter);
    if (!showInactive) params.set('active_only', 'true');
    
    const res = await fetch(`${API}/admin/v3/lifeflo-definitions?${params}`, { headers });
    if (res.ok) {
      const data = await res.json();
      setDefinitions(data.definitions || []);
    }
    setLoading(false);
  }, [filter, showInactive]);
  
  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);
  
  const handleAdd = () => {
    setEditDef(null);
    setShowEditDialog(true);
  };
  
  const handleEdit = (def) => {
    setEditDef(def);
    setShowEditDialog(true);
  };
  
  const handleSave = async (formData) => {
    if (formData.id) {
      await fetch(`${API}/admin/v3/lifeflo-definitions/${formData.id}`, {
        method: 'PUT', headers, body: JSON.stringify(formData)
      });
    } else {
      await fetch(`${API}/admin/v3/lifeflo-definitions`, {
        method: 'POST', headers, body: JSON.stringify(formData)
      });
    }
    fetchDefinitions();
  };
  
  const handleToggleActive = async (def) => {
    await fetch(`${API}/admin/v3/lifeflo-definitions/${def.id}`, {
      method: 'PUT', headers, body: JSON.stringify({ ...def, is_active: !def.is_active })
    });
    fetchDefinitions();
  };
  
  const handleDelete = async (defId) => {
    if (!confirm('Delete this LifeFlō definition?')) return;
    await fetch(`${API}/admin/v3/lifeflo-definitions/${defId}`, {
      method: 'DELETE', headers
    });
    fetchDefinitions();
  };
  
  // Group by category
  const grouped = definitions.reduce((acc, def) => {
    const cat = def.category || 'routine';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(def);
    return acc;
  }, {});
  
  return (
    <AdminLayout>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="lifeflo-definitions">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white/90">LifeFlō Definitions</h1>
            <p className="text-xs text-white/40 mt-1">Manage reusable routine events library</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[160px] bg-adm-surface/5 border-white/10 text-white h-8 text-xs">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                <SelectItem value="all" className="text-white text-xs">All Categories</SelectItem>
                {LIFEFLO_CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value} className="text-white text-xs">
                    <span className={c.color}>{c.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant={showInactive ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setShowInactive(!showInactive)}
              className={`text-xs h-8 ${showInactive ? 'bg-adm-surface/10' : 'border-white/10 text-white/50'}`}
            >
              Show Inactive
            </Button>
            
            <Button onClick={handleAdd} size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white h-8">
              <Plus className="w-3 h-3 mr-1" /> Add Definition
            </Button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-6 gap-3 mb-6">
          {LIFEFLO_CATEGORIES.map(cat => {
            const count = (grouped[cat.value] || []).length;
            const Icon = cat.icon;
            return (
              <Card key={cat.value} className={`bg-adm-surface/[0.02] border-white/[0.06] ${filter === cat.value ? 'ring-1 ring-white/20' : ''}`}>
                <CardContent className="p-3 flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${cat.color}`} />
                  <div>
                    <div className="text-lg font-semibold text-white/90">{count}</div>
                    <div className="text-[9px] text-white/40">{cat.label}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64 text-white/40">Loading...</div>
        ) : definitions.length === 0 ? (
          <div className="text-center py-12 text-white/40">
            <Leaf className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>No LifeFlō definitions found</p>
            <Button onClick={handleAdd} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Add First Definition
            </Button>
          </div>
        ) : filter === 'all' ? (
          // Grouped view
          <div className="space-y-6">
            {LIFEFLO_CATEGORIES.map(cat => {
              const items = grouped[cat.value] || [];
              if (items.length === 0) return null;
              const Icon = cat.icon;
              
              return (
                <div key={cat.value}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`w-4 h-4 ${cat.color}`} />
                    <h2 className={`text-sm font-medium ${cat.color}`}>{cat.label}</h2>
                    <Badge className="bg-adm-surface/5 text-white/40 border-0 text-[9px]">{items.length}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {items.map(def => (
                      <DefinitionCard 
                        key={def.id} 
                        def={def} 
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleActive={handleToggleActive}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Filtered view
          <div className="grid grid-cols-3 gap-3">
            {definitions.map(def => (
              <DefinitionCard 
                key={def.id} 
                def={def} 
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
              />
            ))}
          </div>
        )}
        
        {/* Edit Dialog */}
        <DefinitionEditDialog 
          def={editDef} 
          open={showEditDialog} 
          onClose={() => setShowEditDialog(false)}
          onSave={handleSave}
        />
      </div>
    </AdminLayout>
  );
}
